import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './_carousel.scss';
import BetaIcon, { IconSizes } from '../../component/Icons/Icon';
import debounce from 'lodash.debounce';

const Carousel = ({ classNames, children, withControls }) => {
  const contentRef = useRef(null);

  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragThresholdMet, setDragThresholdMet] = useState(false);

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const checkCarouselPosition = debounce(() => {
    if (contentRef.current) {
      const maxScrollLeft =
        contentRef.current.scrollWidth - contentRef.current.clientWidth;

      setIsAtStart(contentRef.current.scrollLeft === 0);
      setIsAtEnd(Math.round(contentRef.current.scrollLeft) === maxScrollLeft);
    }
  }, 100);

  useEffect(() => {
    const handleClick = (event) => {
      if (isDragging && dragThresholdMet) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const handleMouseDown = (event) => {
      if (event.target.tagName.toLowerCase() === 'input') {
        return;
      }

      if (contentRef.current && contentRef.current.contains(event.target)) {
        event.preventDefault();
        setIsDragging(true);
        setDragThresholdMet(false);
        setStartX(event.pageX - contentRef.current.offsetLeft);
        setScrollLeft(contentRef.current.scrollLeft);
      }
    };

    const handleMouseMove = (event) => {
      if (!isDragging) return;
      if (contentRef.current && contentRef.current.contains(event.target)) {
        event.preventDefault();
        const x = event.pageX - contentRef.current.offsetLeft;
        const walk = x - startX;

        if (Math.abs(walk) > 5) {
          setDragThresholdMet(true);
          contentRef.current.scrollLeft = scrollLeft - walk;
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    contentRef.current.addEventListener('click', handleClick);
    contentRef.current.addEventListener('pointerdown', handleMouseDown);
    document.addEventListener('pointermove', handleMouseMove);
    document.addEventListener('pointerup', handleMouseUp);

    return () => {
      contentRef.current.removeEventListener('click', handleClick);
      contentRef.current.removeEventListener('pointerdown', handleMouseDown);
      document.removeEventListener('pointermove', handleMouseMove);
      document.removeEventListener('pointerup', handleMouseUp);
    };
  }, [isDragging, dragThresholdMet, startX, scrollLeft]);

  const handleNext = () => {
    if (contentRef.current) {
      const itemWidth = contentRef.current.firstChild.offsetWidth;
      contentRef.current.scrollLeft += itemWidth;

      checkCarouselPosition();
    }
  };

  const handlePrev = () => {
    if (contentRef.current) {
      const itemWidth = contentRef.current.firstChild.offsetWidth;
      contentRef.current.scrollLeft -= itemWidth;

      checkCarouselPosition();
    }
  };

  const carousel = (
    <div
      ref={contentRef}
      className={cn(classNames, 'carousel')}
      onScroll={() => withControls && checkCarouselPosition()}
    >
      {children.map((item, index) => {
        return (
          <div key={index} className="carousel--item">
            {item}
          </div>
        );
      })}
    </div>
  );

  const controlled = (
    <div className="carousel--controls-wrapper">
      {!isAtStart && (
        <button
          className="carousel--control carousel--control-prev"
          onClick={handlePrev}
        >
          <BetaIcon name={'chevronLeft'} size={IconSizes[16]} />
        </button>
      )}

      {carousel}

      {!isAtEnd && (
        <button
          className="carousel--control carousel--control-next"
          onClick={handleNext}
        >
          <BetaIcon name={'chevronRight'} size={IconSizes[16]} />
        </button>
      )}
    </div>
  );

  return (
    <div className="carousel--container">
      {withControls ? controlled : carousel}
    </div>
  );
};

Carousel.propTypes = {
  classNames: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  withControls: PropTypes.bool,
};

export default Carousel;
